import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import { companiesApi, eventsApi, invoicesApi } from "../../api";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { useAuth } from "../../context/AuthContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { queryKeys } from "../../queryKeys";
import { isDraftInvoice, invoiceDisplayName } from "../../types";
import type { InvoiceRecord, InvoiceSearchGroup, InvoiceSortKey } from "../../types";
import { formatINR } from "../../utils/format";
import { downloadInvoiceDocument } from "../../utils/invoicePrintDocument";
import {
  loadInvoicePrintProfile,
  profileFromCompany,
  profileToPrintOptions,
  type InvoiceFormProfile,
} from "../../utils/invoicePrintProfile";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import { EmailInvoiceModal } from "../Finance/EmailInvoiceModal";
import "./EventInvoiceManagePage.css";

type Props = {
  workspace: WorkspaceId;
};

const STATUS_PENDING = "Payment Pending";
const STATUS_RECEIVED = "Payment Received";

const SORTS: Array<{ key: InvoiceSortKey; label: string }> = [
  { key: "name", label: "Name" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
];

function paymentLabel(status?: string | null): typeof STATUS_PENDING | typeof STATUS_RECEIVED {
  return (status || "").toLowerCase().includes("received") ? STATUS_RECEIVED : STATUS_PENDING;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Manage Invoices sub-tab.
 *
 * Search and sort run server-side (the same endpoint Finance uses), scoped to
 * this event. Drafts are listed but carry no number and are excluded from the
 * page total — per the sprint, they must not enter financial figures.
 */
const EventInvoiceManagePage: React.FC<Props> = ({ workspace }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { token, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<InvoiceSortKey>("date");
  const [emailTarget, setEmailTarget] = useState<InvoiceRecord | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const listQuery = useQuery({
    queryKey: eventId
      ? queryKeys.invoiceSearch(workspace, eventId, { q: debouncedSearch, sort })
      : queryKeys.events.all(workspace),
    queryFn: () =>
      invoicesApi.search(
        { q: debouncedSearch || undefined, event_id: eventId, sort },
        token!
      ),
    enabled: Boolean(token && eventId),
  });

  // Sourced for the PDF header: seller logo (Profile section) + event details.
  const companyQuery = useQuery({
    queryKey: token ? ["company-me", token] : ["company-me", "idle"],
    queryFn: () => companiesApi.me(token!),
    enabled: Boolean(token),
  });

  const eventQuery = useQuery({
    queryKey: eventId ? queryKeys.events.detail(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => eventsApi.detail(eventId!, token!),
    enabled: Boolean(token && eventId),
  });

  const invoices: InvoiceRecord[] = useMemo(() => {
    const groups: InvoiceSearchGroup[] = listQuery.data ?? [];
    return groups.flatMap((group) => group.invoices);
  }, [listQuery.data]);

  // Drafts are unfinished documents, so they stay out of the total even
  // though they remain visible in the list.
  const issuedTotal = useMemo(
    () =>
      invoices
        .filter((invoice) => !isDraftInvoice(invoice))
        .reduce((sum, invoice) => sum + Number(invoice.total || 0), 0),
    [invoices]
  );
  const draftCount = invoices.filter(isDraftInvoice).length;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["invoices", workspace, eventId] });

  const statusMutation = useMutation({
    mutationFn: ({ invoiceId, status }: { invoiceId: string; status: string }) =>
      invoicesApi.updateStatus(invoiceId, status, token!),
    onSuccess: async () => {
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not update status"),
  });

  const deleteMutation = useMutation({
    mutationFn: (invoiceId: string) => invoicesApi.deleteDraft(invoiceId, token!),
    onSuccess: async () => {
      toast.success("Draft discarded");
      await invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not discard draft"),
  });

  const handleDownload = async (invoice: InvoiceRecord) => {
    if (!token) return;
    setBusyId(invoice.id);
    try {
      const detail = await invoicesApi.detail(invoice.id, token);
      // form_data is the server copy; the localStorage profile is the legacy
      // fallback for invoices generated before it was persisted.
      const local = loadInvoicePrintProfile(invoice.id);
      const savedProfile = (detail.form_data as unknown as InvoiceFormProfile | null) ?? local;
      // Seller identity/bank fields are never entered on the invoice form
      // itself (BusinessInfoPanel only displays them, read-only) — they live
      // solely on the Company profile, so they must be merged in here or the
      // PDF's seller block renders blank regardless of what's saved.
      const stored = companyQuery.data
        ? profileFromCompany(companyQuery.data, savedProfile ?? undefined)
        : savedProfile;

      // One-time recovery: this data only ever existed in the browser that
      // created the invoice, so there is no server-side backfill available.
      // Push it up as invoices are opened rather than leaving it stranded.
      if (!detail.form_data && local) {
        void invoicesApi
          .updateDraft(invoice.id, { form_data: local as unknown as Record<string, unknown> }, token)
          .catch(() => {
            // Issued invoices reject PATCH by design (409). Nothing to
            // recover in that case, and the download still works from local.
          });
      }

      const event = eventQuery.data;
      const sellerLogoUrl = companyQuery.data?.logo_filename
        ? await companiesApi
            .downloadLogo(token)
            .then(blobToDataUrl)
            .catch(() => undefined)
        : undefined;
      await downloadInvoiceDocument(detail, {
        ...(stored ? profileToPrintOptions(stored, detail) : {}),
        sellerLogoUrl,
        sellerPhone: user?.phone,
        sellerEmail: user?.email,
        buyerSameAsConsignee: stored?.consigneeSameAsBuyer,
        eventName: event?.name,
        eventDate: event?.event_date,
        eventLocation: event?.city,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not download invoice");
    } finally {
      setBusyId(null);
    }
  };

  if (!eventId) return null;

  return (
    <div className="event-invoice-manage">
      <div className="event-invoice-manage__toolbar">
        <input
          type="search"
          className="eventos-input event-invoice-manage__search"
          placeholder="Search consignee or invoice number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search invoices"
        />
        <div className="event-invoice-manage__sorts" role="group" aria-label="Sort invoices">
          {SORTS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`eventos-nav-btn${sort === option.key ? " active" : ""}`}
              aria-pressed={sort === option.key}
              onClick={() => setSort((current) => (current === option.key ? "date" : option.key))}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {listQuery.isError ? (
        <QueryErrorBanner
          message={
            listQuery.error instanceof Error ? listQuery.error.message : "Failed to load invoices"
          }
          onRetry={() => void listQuery.refetch()}
        />
      ) : null}

      <div className="event-invoice-manage__summary eventos-subtle">
        {invoices.length} invoice{invoices.length === 1 ? "" : "s"} · issued total {formatINR(issuedTotal)}
        {draftCount > 0 ? ` · ${draftCount} draft${draftCount === 1 ? "" : "s"} excluded` : ""}
      </div>

      {listQuery.isPending ? (
        <p className="eventos-subtle">Loading invoices…</p>
      ) : invoices.length === 0 ? (
        <div className="eventos-empty">No invoices yet.</div>
      ) : (
        <ul className="event-invoice-manage__list">
          {invoices.map((invoice) => {
            const draft = isDraftInvoice(invoice);
            const status = paymentLabel(invoice.status);
            return (
              <li key={invoice.id} className={`event-invoice-manage__row${draft ? " is-draft" : ""}`}>
                <div className="event-invoice-manage__main">
                  <div className="event-invoice-manage__name">
                    {invoiceDisplayName(invoice)}
                    <span className="event-invoice-manage__number">
                      {invoice.invoice_number ? ` — ${invoice.invoice_number}` : ""}
                    </span>
                    {draft ? <span className="event-invoice-manage__badge">Draft</span> : null}
                  </div>
                  <div className="event-invoice-manage__amount">
                    {formatINR(invoice.total)}
                    {invoice.due_date ? (
                      <span className="eventos-subtle event-invoice-manage__due">
                        {" "}
                        · Due {new Date(invoice.due_date).toLocaleDateString("en-IN")}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="event-invoice-manage__actions">
                  {draft ? (
                    <>
                      <button
                        type="button"
                        className="eventos-nav-btn"
                        onClick={() =>
                          navigate(`/events/${eventId}/invoices/generate?draft=${invoice.id}`)
                        }
                      >
                        Continue editing
                      </button>
                      <button
                        type="button"
                        className="eventos-nav-btn"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(invoice.id)}
                      >
                        Discard
                      </button>
                      {/* A draft has no number and no PDF, so emailing and
                          downloading are meaningless until it is finalized. */}
                      <span className="eventos-subtle event-invoice-manage__draft-hint">
                        Finalize to email or download
                      </span>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="eventos-nav-btn"
                        onClick={() => setEmailTarget(invoice)}
                        title={
                          invoice.sent_at
                            ? `Last sent ${new Date(invoice.sent_at).toLocaleString("en-IN")}${
                                invoice.sent_to ? ` to ${invoice.sent_to}` : ""
                              }`
                            : "Not sent yet"
                        }
                      >
                        Email invoice{invoice.sent_at ? " ✓" : ""}
                      </button>
                      <button
                        type="button"
                        className="eventos-nav-btn"
                        disabled={busyId === invoice.id}
                        onClick={() => void handleDownload(invoice)}
                      >
                        {busyId === invoice.id ? "Downloading…" : "Download"}
                      </button>
                      <button
                        type="button"
                        className={`event-invoice-manage__status${
                          status === STATUS_RECEIVED ? " is-received" : ""
                        }`}
                        disabled={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            invoiceId: invoice.id,
                            status: status === STATUS_RECEIVED ? STATUS_PENDING : STATUS_RECEIVED,
                          })
                        }
                      >
                        {status}
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {emailTarget ? (
        <EmailInvoiceModal
          invoice={emailTarget}
          token={token}
          onClose={() => setEmailTarget(null)}
          onSent={() => {
            setEmailTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
};

export default EventInvoiceManagePage;
